import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import api from '../services/api';

interface DocEntry { filename: string; title: string; }

/** Sidebar section config — maps filename → { section, subsection, labelKey, order }.
 *  Docs not listed here appear below the sections as a flat ungrouped list. */
interface SidebarItemConfig { section: string; subsection: string; labelKey: string; order: number; }
const SIDEBAR_CONFIG: Record<string, SidebarItemConfig> = {
  'SOLUTION_DESCRIPTION_GUIDE.md':   { section: 'guides', subsection: 'solution', labelKey: 'help.items.solutionDescription',     order: 0 },
  'SOTA_WORKFLOW_GUIDE.md':          { section: 'guides', subsection: 'sota',     labelKey: 'help.items.sotaWorkflow',            order: 0 },
  'CLAIMS_GENERATION_GUIDE.md':      { section: 'guides', subsection: 'solution', labelKey: 'help.items.claims',                  order: 1 },
  'FTO_ANALYSIS.md':                 { section: 'guides', subsection: 'solution', labelKey: 'help.items.ftoAnalysis',             order: 2 },
  'PATENTABILITY_ANALYSIS.md':       { section: 'guides', subsection: 'solution', labelKey: 'help.items.patentabilityAnalysis',   order: 3 },
  'FIGURES_GUIDE.md':                { section: 'guides', subsection: 'solution', labelKey: 'help.items.figures',                 order: 4 },
  'SEARCH_METHODS.md':               { section: 'guides', subsection: 'search',   labelKey: 'help.items.searchMethods',           order: 5 },
  'SECURITY.md':                     { section: 'admin',  subsection: 'system',   labelKey: 'help.items.security',                order: 0 },
  'PROMPTS_MANAGEMENT.md':           { section: 'admin',  subsection: 'system',   labelKey: 'help.items.promptsManagement',        order: 1 },
  'RELEVANCE_ASSESSMENT.md':         { section: 'admin',  subsection: 'system',   labelKey: 'help.items.relevanceAssessment',      order: 2 },
};

export default function Help() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [docs, setDocs] = useState<DocEntry[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Fetch the list of available documents from the backend
  useEffect(() => {
    api.get<DocEntry[]>('/help/docs')
      .then(r => {
        setDocs(r.data);
        const requested = searchParams.get('doc');
        const match = requested && r.data.find(d => d.filename === requested);
        setSelected(match ? match.filename : r.data.length > 0 ? r.data[0].filename : null);
      })
      .catch(() => setError(t('help.errorLoad', 'Failed to load help documents.')));
  }, []);

  // Fetch raw Markdown content when selection changes
  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    api.get<string>(`/help/files/${selected}`, { responseType: 'text' })
      .then(r => setContent(typeof r.data === 'string' ? r.data : JSON.stringify(r.data)))
      .catch(err => {
        if (err.response?.status === 403) {
          setError(t('help.errorForbidden', 'You do not have permission to view this document.'));
        } else {
          setError(t('help.errorDoc', 'Failed to load document.'));
        }
      })
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Doc list */}
      <aside className="w-56 flex-shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto py-3">
        <p className="px-4 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
          {t('help.sidebarTitle', 'Documentation')}
        </p>

        {/* --- Sectioned docs (Guides > Solution > ...) --- */}
        {(() => {
          // Build section → subsection → DocEntry[] tree for docs present in API response
          type Tree = Map<string, Map<string, DocEntry[]>>;
          const tree: Tree = new Map();
          for (const doc of docs) {
            const cfg = SIDEBAR_CONFIG[doc.filename];
            if (!cfg) continue;
            if (!tree.has(cfg.section)) tree.set(cfg.section, new Map());
            const sub = tree.get(cfg.section)!;
            if (!sub.has(cfg.subsection)) sub.set(cfg.subsection, []);
            sub.get(cfg.subsection)!.push(doc);
          }
          // Sort each subsection by config order
          tree.forEach(subs => subs.forEach(items =>
            items.sort((a, b) => (SIDEBAR_CONFIG[a.filename]?.order ?? 99) - (SIDEBAR_CONFIG[b.filename]?.order ?? 99))
          ));

          return Array.from(tree.entries()).map(([sectionKey, subs]) => (
            <div key={sectionKey} className="mb-1">
              <p className="px-4 pt-3 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {t(`help.sections.${sectionKey}`, sectionKey)}
              </p>
              {Array.from(subs.entries()).map(([subKey, items]) => (
                <div key={subKey}>
                  <p className="px-6 pt-1 pb-0.5 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    {t(`help.subsections.${subKey}`, subKey)}
                  </p>
                  {items.map(d => (
                    <button
                      key={d.filename}
                      onClick={() => setSelected(d.filename)}
                      className={`w-full text-left pl-8 pr-3 py-1.5 text-sm transition-colors ${
                        selected === d.filename
                          ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent'
                      }`}
                    >
                      {t(SIDEBAR_CONFIG[d.filename].labelKey, d.title)}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ));
        })()}

        {/* --- Ungrouped docs (flat list, as before) --- */}
        {docs.filter(d => !SIDEBAR_CONFIG[d.filename]).map(d => (
          <button
            key={d.filename}
            onClick={() => setSelected(d.filename)}
            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
              selected === d.filename
                ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent'
            }`}
          >
            {d.title}
          </button>
        ))}
      </aside>

      {/* Content */}
      <main ref={mainRef} className="flex-1 overflow-y-auto px-8 py-6">
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {loading && <p className="text-slate-400 text-sm">{t('common.loading', 'Loading...')}</p>}
        {!loading && !error && content && (
          <article className="prose prose-slate prose-sm max-w-3xl prose-headings:font-semibold prose-table:text-sm">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
              components={{
                a: ({ href, children, ...props }) => {
                  if (href?.startsWith('#')) {
                    return (
                      <a
                        href={href}
                        onClick={e => {
                          e.preventDefault();
                          const id = href.slice(1);
                          const target = mainRef.current?.querySelector(`[id="${id}"]`);
                          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                  return <a href={href} {...props}>{children}</a>;
                },
              }}
            >
              {content}
            </ReactMarkdown>
          </article>
        )}
      </main>
    </div>
  );
}
